import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TablePagination
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GetAppIcon from "@mui/icons-material/GetApp";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageCostCenters = () => {
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form state
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("Active");
  const [department, setDepartment] = useState("");
  const [description, setDescription] = useState("");
  const [agreementFile, setAgreementFile] = useState(null);

  // Dialog state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingCostCenter, setEditingCostCenter] = useState(null);

  useEffect(() => {
    fetchCostCenters();
  }, [page, limit]);

  const fetchCostCenters = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/cost-centers?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setCostCenters(response.data.data || []);
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.total);
        } else {
          setTotalCount(response.data.data.length);
        }
      }
    } catch (err) {
      //console.error("Error fetching cost centers:", err);
      setError(err.response?.data?.message || "Failed to load cost centers");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCode("");
    setStatus("Active");
    setDepartment("");
    setDescription("");
    setAgreementFile(null);
    setEditingCostCenter(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      setError("Please fill out Name and Code.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("code", code.trim().toUpperCase());
      formData.append("status", status);
      formData.append("department", department.trim());
      formData.append("description", description.trim());
      if (agreementFile) {
        formData.append("agreementFile", agreementFile);
      }

      const response = await axios.post(
        `${API_URL}/api/cost-centers`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setSuccess("Cost Center created successfully!");
        setOpenAddDialog(false);
        resetForm();
        fetchCostCenters();
      }
    } catch (err) {
      //console.error("Error creating cost center:", err);
      setError(err.response?.data?.message || "Failed to create cost center");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (cc) => {
    setEditingCostCenter(cc);
    setName(cc.name);
    setCode(cc.code);
    setStatus(cc.status);
    setDepartment(cc.department || "");
    setDescription(cc.description || "");
    setAgreementFile(null);
    setOpenEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      setError("Please fill out Name and Code.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("code", code.trim().toUpperCase());
      formData.append("status", status);
      formData.append("department", department.trim());
      formData.append("description", description.trim());
      if (agreementFile) {
        formData.append("agreementFile", agreementFile);
      }

      const response = await axios.put(
        `${API_URL}/api/cost-centers/${editingCostCenter.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setSuccess("Cost Center updated successfully!");
        setOpenEditDialog(false);
        resetForm();
        fetchCostCenters();
      }
    } catch (err) {
      //console.error("Error updating cost center:", err);
      setError(err.response?.data?.message || "Failed to update cost center");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the Cost Center "${name}"?`)) {
      return;
    }

    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API_URL}/api/cost-centers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setSuccess("Cost Center deleted successfully!");
        fetchCostCenters();
      }
    } catch (err) {
      //console.error("Error deleting cost center:", err);
      setError(err.response?.data?.message || "Failed to delete cost center");
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="text.primary">
          Manage Cost Centers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpenAddDialog(true);
          }}
          sx={{
            py: 1,
            px: 2,
            textTransform: "none",
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          Add Cost Center
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      ) : costCenters.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No cost centers configured. Click "Add Cost Center" to create one.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "grey.100" }}>
                <TableRow>
                  <TableCell><strong>Cost Center Name</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Agreement</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costCenters.map((cc) => (
                  <TableRow key={cc.id} hover>
                    <TableCell><strong>{cc.name}</strong></TableCell>
                    <TableCell>{cc.code}</TableCell>
                    <TableCell>{cc.department || "-"}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.85rem",
                          fontWeight: "500",
                          backgroundColor: cc.status === "Active" ? "#e8f5e9" : "#ffebee",
                          color: cc.status === "Active" ? "#2e7d32" : "#c62828",
                        }}
                      >
                        {cc.status}
                      </Box>
                    </TableCell>
                    <TableCell>{cc.description || "-"}</TableCell>
                    <TableCell>
                      {cc.agreementPath ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={cc.agreementPath.startsWith('http') ? cc.agreementPath : `${API_URL}/uploads/${cc.agreementPath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontSize: "0.8rem", py: 0.25 }}
                        >
                          View
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No File
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{new Date(cc.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEdit(cc)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(cc.id, cc.name)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(1);
            }}
          />
        </Box>
      )}

      {/* Add Cost Center Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Add New Cost Center
            </Typography>
            <IconButton onClick={() => setOpenAddDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleCreate} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Cost Center Name"
                  placeholder="e.g., Engineering, Sales, HR"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Cost Center Code"
                  placeholder="e.g., CC-ENG, CC-SALES, CC-HR"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  variant="outlined"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Department"
                  placeholder="e.g., Engineering, Sales, HR (Optional)"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  placeholder="Describe the purpose of this Cost Center... (Optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
                  Agreement Document (Optional)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderStyle: "dashed" }}
                >
                  {agreementFile ? agreementFile.name : "Choose File (PDF, Word, Images)"}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setAgreementFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.rtf,.txt,.png,.jpg,.jpeg"
                  />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)} color="inherit" variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={submitting || !name.trim() || !code.trim()}
          >
            {submitting ? "Creating..." : "Save Cost Center"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cost Center Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Edit Cost Center
            </Typography>
            <IconButton onClick={() => setOpenEditDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleUpdate} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Cost Center Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Cost Center Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  variant="outlined"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
                  Agreement Document (Upload to replace existing document)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderStyle: "dashed" }}
                >
                  {agreementFile ? agreementFile.name : "Choose New File"}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setAgreementFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.rtf,.txt,.png,.jpg,.jpeg"
                  />
                </Button>
                {editingCostCenter?.agreementPath && !agreementFile && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Current File: {editingCostCenter.agreementPath.substring(13)} {/* Strip timestamp prefix */}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)} color="inherit" variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={submitting || !name.trim() || !code.trim()}
          >
            {submitting ? "Updating..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageCostCenters;
